import { useCallback, useState, useEffect } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import { ROLES } from 'utils/Constants';

export const TextDetailsSection = (props) => {
  const {
    t,
    formData = {},
    handlers = {},
    disabledFields = [],
    errors = {},
    textId, // ID du texte pour l'appel API
    userRole,
    metaMaxLength,
    expandMetaField = false,
  } = props || {};

  const textTitle = formData?.textTitle ?? '';
  const keyword = formData?.keyword ?? '';
  const url = formData?.url ?? '';

  // Champs "paramètres" supplémentaires (si présents dans formData)
  const redactor = formData?.redactor ?? formData?.writerName ?? '';
  const aiAgent = formData?.aiAgent ?? formData?.agentIa ?? '';
  const contentType = formData?.contentType ?? '';

  const projectType = (formData?.projectType || formData?.orderType || '').toUpperCase();
  const isSmartProject = projectType.startsWith('SMART');
  const isPremiumProject = projectType === 'PREMIUM';

  const [showSeoTags, setShowSeoTags] = useState(false);
  const [showUrlSection, setShowUrlSection] = useState(false);
  const [titleTag, setTitleTag] = useState('');
  const [metaDescriptionTag, setMetaDescriptionTag] = useState('');

  // États pour les données de l'API
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [environment, setEnvironment] = useState('');

  const isWriterRole =
    userRole === ROLES.WRITER || userRole === ROLES.GLOBAL_WRITER;
  const useExpandedMeta = expandMetaField || isWriterRole;

  const TITLE_MAX = 120;
  const metaCharLimit = useExpandedMeta
    ? metaMaxLength ?? null
    : metaMaxLength ?? 120;

  const titleLength = titleTag?.length || 0;
  const metaLength = metaDescriptionTag?.length || 0;

  const [isEditingKeyword, setIsEditingKeyword] = useState(false);
  const [keywordDraft, setKeywordDraft] = useState(keyword || '');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('AC_Dashboard_SelectedWebsite');
      if (!raw) return;

      try {
        const parsed = JSON.parse(raw);
        const value =
          parsed?.name || parsed?.urlSite || parsed?.url || String(raw);
        setEnvironment(value);
      } catch {
        setEnvironment(raw);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du localStorage:', error);
    }
  }, []);

  // Appel API pour récupérer les données du projet
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!textId) return;

      setLoading(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/${textId}/project-summary`
        );
        const data = response?.data || {};
        setProjectData(data);
      } catch (error) {
        console.error('Erreur API project-summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [textId]);

  useEffect(() => {
    if (formData?.titleTag !== undefined) {
      setTitleTag(formData.titleTag);
    }
  }, [formData?.titleTag]);

  useEffect(() => {
    if (formData?.metaDescriptionTag !== undefined) {
      setMetaDescriptionTag(formData.metaDescriptionTag);
    }
  }, [formData?.metaDescriptionTag]);

  // Sync du draft quand le mot-clé source change (hors édition)
  useEffect(() => {
    if (!isEditingKeyword) {
      setKeywordDraft(keyword || '');
    }
  }, [keyword, isEditingKeyword]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    const limitedValue = value.slice(0, TITLE_MAX);
    setTitleTag(limitedValue);

    if (handlers?.setTitleTag) {
      handlers.setTitleTag(limitedValue);
    }
    if (handlers?.onFieldChange) {
      handlers.onFieldChange('titleTag', limitedValue);
    }
  };

  const handleMetaChange = (e) => {
    const value = e.target.value;
    const limitedValue =
      metaCharLimit != null ? value.slice(0, metaCharLimit) : value;
    setMetaDescriptionTag(limitedValue);

    if (handlers?.setMetaDescriptionTag) {
      handlers.setMetaDescriptionTag(limitedValue);
    }
    if (handlers?.onFieldChange) {
      handlers.onFieldChange('metaDescriptionTag', limitedValue);
    }
  };

  const handleKeywordChange = (e) => {
    const value = e.target.value;
    if (handlers?.setKeyword) {
      handlers.setKeyword(value);
    }
    if (handlers?.onFieldChange) {
      handlers.onFieldChange('keyword', value);
    }
  };

  const handleUrlChange = (e) => {
    const value = e.target.value;
    if (handlers?.setUrl) {
      handlers.setUrl(value);
    }
    if (handlers?.onFieldChange) {
      handlers.onFieldChange('url', value);
    }
  };

  const startKeywordEdit = () => {
    if (disabledFields.includes('keyword')) return;
    setKeywordDraft(keyword || '');
    setIsEditingKeyword(true);
  };

  const confirmKeywordEdit = () => {
    handleKeywordChange({ target: { value: keywordDraft || '' } });
    setIsEditingKeyword(false);
  };

  const getWriterName = () => {
    if (!projectData?.writer) return null;
    const writer = projectData.writer;
    return writer.name || writer.mail || writer.id || null;
  };

  const getAiAgentName = () => {
    if (projectData?.projectAiAgent) {
      return projectData.projectAiAgent.name;
    }
    return null;
  };

  const getMaxWords = () => {
    if (projectData?.projectMaxWords) return projectData.projectMaxWords;
    return "";
  };

  const getLLMType = () => {
    return projectData?.projectLLMType || '';
  };

  return (
    <>
      <style>{`
        .textdetails-display {
          max-width: 2500px;
          width: 100%;
        }
        .textdetails-title {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 20px;
          font-weight: 600;
          color: #000;
          margin-bottom: 4px;
          margin-left: 20px;
          cursor: pointer;
        }

        .textdetails-title-text {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .textdetails-title-edit-icon {
          font-size: 13px;
          color: #9CA3AF;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .textdetails-title:hover .textdetails-title-edit-icon {
          opacity: 1;
        }

        .textdetails-title-edit {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-left: 20px;
          margin-bottom: 4px;
        }

        .textdetails-title-input {
          padding: 8px 10px;
          border-radius: 6px;
          border: 1px solid #D1D5DB;
          font-size: 14px;
          min-width: 800px;
        }

        .textdetails-title-input:focus {
          outline: none;
          border-color: #7c3aed;
        }

        .textdetails-title-check {
          border: none;
          background-color: transparent;
          color: #111827;
          border-radius: 0;
          padding: 0 4px;
          font-size: 16px;
          cursor: pointer;
        }
        .textdetails-badge {
          display: inline-flex;
          align-items: center;
          background-color: #e8d4ff;
          color: #7c3aed;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 500;
          margin-left: 10px;
        }
        .textdetails-badge::before {
          content: "✨";
          margin-right: 5px;
        }
        .textdetails-info {
          color: #666;
          font-size: 14px;
          margin-bottom: 15px;
          line-height: 1.6;
          margin-left: 20px;
        }
        .textdetails-info span {
          margin-right: 8px;
        }
        .textdetails-info span::after {
          content: "•";
          margin-left: 8px;
          color: #999;
        }
        .textdetails-info span:last-child::after {
          content: "";
        }

        .textdetails-buttons {
          display: flex;
          gap: 10px;
          margin-left: 20px;
        }
        .textdetails-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 16px;
          background-color: #F1F1F1;
          border: 1px solid #EEEEEE;
          border-radius: 50px;
          font-size: 14px;
          color: #333;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(228, 227, 227, 0.85);
          margin-bottom: 10px;
          margin-top: 10px;
        }
      
        .textdetails-btn svg {
          width: 16px;
          height: 16px;
        }

        /* Bouton / icône Paramètres */
        .textdetails-settings-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background-color: transparent;
          border: none;
          border-radius: 999px;
          font-size: 14px;
          color: #555;
          cursor: pointer;
          transition: color 0.2s, background-color 0.2s;
          margin-left: 1100px;
        }

        .textdetails-settings-icon {
          width: 18px;
          height: 18px;
        }

        .textdetails-modal {
          margin-top: 15px;
          margin-left: 20px;
          margin-right: 20px;
          padding: 0;
          background-color: transparent;
          border: none;
          box-shadow: none;
          margin-bottom: 10px;
        }
        .textdetails-field {
          margin-bottom: 18px;
          padding: 16px 18px;
          background: #FAFAFA;
          border-radius: 8px;
          border: 1px solid #E9E9E9;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          margin-bottom: 10px;
        }
        .textdetails-field:last-child {
          margin-bottom: 0;
        }
        .textdetails-field label {
          display: block;
          font-weight: 600;
          font-size: 14px;
          color: #333;
          margin-bottom: 8px;
        }
        .textdetails-field input,
        .textdetails-field textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          color: #333;
          transition: border-color 0.2s;
          background-color: white;
        }
        .textdetails-field input:focus,
        .textdetails-field textarea:focus {
          outline: none;
          border-color: #7c3aed;
        }
        .textdetails-field textarea {
          resize: vertical;
          min-height: 80px;
        }

        .textdetails-modal-seo-expanded {
          width: 100%;
          max-width: 100%;
          margin-left: 0;
          margin-right: 0;
          box-sizing: border-box;
        }

        .textdetails-modal-seo-expanded .textdetails-field {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .textdetails-modal-seo-expanded .textdetails-field input,
        .textdetails-modal-seo-expanded .textdetails-field textarea {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .textdetails-modal-seo-expanded .textdetails-field-meta textarea {
          min-height: 100px;
          resize: vertical;
        }

        .textdetails-settings-row {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .textdetails-settings-row .textdetails-field {
          flex: 1 1 0;
          margin-bottom: 0;
        }

        /* Compteur de caractères sous les champs */
        .textdetails-charcount {
          margin-top: 6px;
          font-size: 12px;
          font-weight: 500;
          color: #16a34a; /* vert par défaut */
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .textdetails-charcount-badge {
          padding: 2px 8px;
          border-radius: 999px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
        }

        .textdetails-loading {
          color: #999;
          font-style: italic;
        }
      `}</style>
      <div className="row">
        <div className="col-12">
          <div className="box-wrapper-editor textdetails-display">
            {isEditingKeyword ? (
              <div className="textdetails-title-edit" style={{ marginTop: 20 }}>
                <input
                  type="text"
                  className="textdetails-title-input"
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  placeholder="Entrez le mot-clé principal"
                  disabled={disabledFields.includes('keyword')}
                />
                <button
                  type="button"
                  className="textdetails-title-check"
                  onClick={confirmKeywordEdit}
                  disabled={disabledFields.includes('keyword')}
                >
                  ✓
                </button>
                {isSmartProject && (
                  <span className="textdetails-badge">Généré par IA</span>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: '15px', marginTop: 20 }}>
                <span
                  className="textdetails-title"
                  onClick={disabledFields.includes('keyword') ? undefined : startKeywordEdit}
                  title={disabledFields.includes('keyword') ? undefined : 'Modifier le mot-clé principal'}
                  style={{ cursor: disabledFields.includes('keyword') ? 'default' : 'pointer' }}
                >
                  <span className="textdetails-title-text">
                    {keyword || textTitle}
                  </span>
                  {!disabledFields.includes('keyword') && (
                    <span className="textdetails-title-edit-icon">✎</span>
                  )}
                </span>
                {isSmartProject && (
                  <span className="textdetails-badge">Généré par IA</span>
                )}
              </div>
            )}

            <div className="textdetails-info">
              {loading ? (
                <span className="textdetails-loading">Chargement...</span>
              ) : (
                <>
                  <span>Environnement : {environment || 'Non défini'}</span>
                  {getWriterName() && (
                    <span>Rédacteur → {getWriterName()}</span>
                  )}
                  {getAiAgentName() && (
                    <span>
                      IA → {getAiAgentName()}{' '}
                      {getLLMType() && `(${getLLMType()})`}
                    </span>
                  )}
                  <span>{getMaxWords()} {t('mots')}</span>
                </>
              )}
            </div>

            <div className="textdetails-buttons">
              <button
                className="textdetails-btn"
                onClick={() => setShowSeoTags(!showSeoTags)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {t('Balise Title & Meta')}
              </button>
              <button
                type="button"
                className="textdetails-btn"
                onClick={() => setShowUrlSection(!showUrlSection)}
                title={t("URL cible")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {t('URL cible')}
              </button>
            </div>

            {showSeoTags && (
              <div
                className={`textdetails-modal textdetails-modal-seo${
                  useExpandedMeta ? ' textdetails-modal-seo-expanded' : ''
                }`}
              >
                <div className="textdetails-field">
                  <label htmlFor="titleTag">{t('Balise Title :')}</label>
                  <input
                    type="text"
                    id="titleTag"
                    value={titleTag}
                    maxLength={TITLE_MAX}
                    onChange={handleTitleChange}
                    placeholder="Entrez la balise title"
                    disabled={disabledFields.includes('titleTag')}
                  />
                  <div className="textdetails-charcount">
                    <span className="textdetails-charcount-badge">
                      {titleLength} / {TITLE_MAX} {t('caractères')}
                    </span>
                  </div>
                </div>

                <div
                  className={`textdetails-field${
                    useExpandedMeta ? ' textdetails-field-meta' : ''
                  }`}
                >
                  <label htmlFor="metaDescriptionTag">{t('Balise Meta Description :')}</label>
                  {useExpandedMeta ? (
                    <textarea
                      id="metaDescriptionTag"
                      rows={4}
                      value={metaDescriptionTag}
                      {...(metaCharLimit != null
                        ? { maxLength: metaCharLimit }
                        : {})}
                      onChange={handleMetaChange}
                      placeholder={t('Entrez la meta description')}
                      disabled={disabledFields.includes('metaDescriptionTag')}
                    />
                  ) : (
                    <input
                      type="text"
                      id="metaDescriptionTag"
                      value={metaDescriptionTag}
                      {...(metaCharLimit != null
                        ? { maxLength: metaCharLimit }
                        : {})}
                      onChange={handleMetaChange}
                      placeholder={t('Entrez la meta description')}
                      disabled={disabledFields.includes('metaDescriptionTag')}
                    />
                  )}
                  <div className="textdetails-charcount">
                    <span className="textdetails-charcount-badge">
                      {metaCharLimit != null
                        ? `${metaLength} / ${metaCharLimit} ${t('caractères')}`
                        : `${metaLength} ${t('caractères')}`}
                    </span>
                  </div>
                </div>

              </div>
            )}

            {showUrlSection && (
              <div className="textdetails-modal">
                <div className="textdetails-field">
                  <label htmlFor="urlField">{t('URL cible :')}</label>
                  <input
                    id="urlField"
                    type="text"
                    value={url}
                    onChange={handleUrlChange}
                    placeholder={t("Entrez l'URL cible")}
                    disabled={disabledFields.includes('url')}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
